import { useDemoSender } from "@/api";
import { locationAtom } from "@/atoms/recommendationAtom";
import { userAtom } from "@/atoms/userAtom";
import { InteractionItem, InteractionType } from "@/generatedApi/userUserInteractionLoggerApi";
import { trackSpotInteraction } from "@/services/analytics";
import { requireAuth } from "@/utils/authErrors";
import { useAtomValue, useSetAtom } from "jotai";
import { useCallback } from "react";
import { InteractionMethod } from "@/constants/swipeConstants";
import { highlightSavedAtom, highlightVisitedAtom } from "@/atoms/interactionHighlightAtom";

type SpotInteractionOptions = {
  onInteractionChange?: () => void;
  spotName?: string;
  spotDistance?: number;
  cardPosition?: number;
  recommendationId?: string;
  totalRecommendations?: number;
};

export const useSpotInteraction = (options: SpotInteractionOptions = {}) => {
  const {
    onInteractionChange,
    spotName,
    spotDistance,
    cardPosition,
    recommendationId,
    totalRecommendations
  } = options;

  const { sender: createInteractionItem } = useDemoSender({
    apiName: "user_interaction_logger",
    serviceName: "default",
    operationId: "createInteractionItem"
  });

  const { sender: deleteInteractionItem } = useDemoSender({
    apiName: "user_interaction_logger",
    serviceName: "default",
    operationId: "deleteInteractionItem"
  });

  const user = useAtomValue(userAtom);
  const location = useAtomValue(locationAtom);
  const highlightSaved = useSetAtom(highlightSavedAtom);
  const highlightVisited = useSetAtom(highlightVisitedAtom);

  // 新しいインタラクションを作成（ホームページ用）
  const createInteraction = useCallback(
    async (
      spotId: string,
      interactionType: InteractionType,
      interactionMethod: InteractionMethod = "swipe"
    ) => {
      // 認証チェック
      if (!requireAuth(user)) {
        return null;
      }

      // requireAuthチェック後、userは必ず存在しuserIdも存在
      const authenticatedUser = user as { userId: string; username?: string };

      try {
        // GAとAPIの両方に送るメタデータを構築
        const metaData = {
          user_latitude: location.latitude,
          user_longitude: location.longitude,
          spot_distance: spotDistance || null,
          card_position: cardPosition || null,
          total_recommendations: totalRecommendations || null,
          interaction_method: interactionMethod,
          timestamp: new Date().toISOString()
        };

        const res = await createInteractionItem({
          requestBody: {
            interaction_type: interactionType,
            user_id: authenticatedUser.userId,
            recommendation_id: recommendationId || "",
            spot_id: spotId,
            data: JSON.stringify(metaData)
          }
        });

        // Track GA event（メタデータを再利用）
        trackSpotInteraction({
          user_id: authenticatedUser.userId,
          spot_id: spotId,
          spot_name: spotName || "Unknown",
          recommendation_id: recommendationId || "",
          interaction_type: interactionType === InteractionType.SAVED ? "saved" : "visited",
          action_type: "create",
          ...metaData
        });

        // アイコン強調表示をトリガー
        if (interactionType === InteractionType.SAVED) {
          highlightSaved();
        } else if (interactionType === InteractionType.VISITED) {
          highlightVisited();
        }

        onInteractionChange?.();
        return res?.interaction_id || null;
      } catch (error) {
        console.error("Error creating interaction item:", error);
        throw new Error("Failed to create interaction item");
      }
    },
    [
      createInteractionItem,
      user,
      location,
      recommendationId,
      spotName,
      spotDistance,
      cardPosition,
      totalRecommendations,
      onInteractionChange,
      highlightSaved,
      highlightVisited
    ]
  );

  // 既存のインタラクションを削除
  const deleteInteraction = useCallback(
    async (interactionItem: InteractionItem, pageType?: "saved" | "visited") => {
      // 認証チェック
      if (!requireAuth(user)) {
        return;
      }

      // requireAuthチェック後、userは必ず存在しuserIdも存在
      const authenticatedUser = user as { userId: string; username?: string };

      try {
        await deleteInteractionItem({
          interactionId: interactionItem.interaction_id
        });

        // Track GA event for deletion
        trackSpotInteraction({
          user_id: authenticatedUser.userId,
          spot_id: interactionItem.spot_id,
          spot_name: spotName || "Unknown",
          recommendation_id: interactionItem.recommendation_id || "",
          interaction_type: pageType === "saved" ? "saved" : "visited",
          action_type: "cancel",
          interaction_method: "swipe",
          user_latitude: location.latitude,
          user_longitude: location.longitude,
          spot_distance: null,
          card_position: null,
          total_recommendations: totalRecommendations || null
        });

        onInteractionChange?.();
      } catch (error) {
        console.error("Error deleting interaction item:", error);
        throw new Error("削除に失敗しました");
      }
    },
    [deleteInteractionItem, user, location, spotName, totalRecommendations, onInteractionChange]
  );

  // インタラクションタイプを変更（削除→新規作成）
  const changeInteractionType = useCallback(
    async (
      interactionItem: InteractionItem,
      newType: InteractionType,
      method?: "swipe_to_saved" | "swipe_to_visited" | "tap_to_saved" | "tap_to_visited"
    ) => {
      // 認証チェック
      if (!requireAuth(user)) {
        return;
      }

      // requireAuthチェック後、userは必ず存在しuserIdも存在
      const authenticatedUser = user as { userId: string; username?: string };

      try {
        // 既存のinteractionを削除
        await deleteInteractionItem({
          interactionId: interactionItem.interaction_id
        });

        // 移動方向に応じたinteraction_methodを決定（引数で指定されていない場合はスワイプとして扱う）
        const interactionMethod: InteractionMethod =
          method || (newType === InteractionType.SAVED ? "swipe_to_saved" : "swipe_to_visited");

        // 新しいタイプでinteractionを作成（メタデータも含める）
        const metaData = {
          user_latitude: location.latitude,
          user_longitude: location.longitude,
          spot_distance: null,
          card_position: null,
          total_recommendations: totalRecommendations || null,
          interaction_method: interactionMethod,
          timestamp: new Date().toISOString()
        };

        await createInteractionItem({
          requestBody: {
            interaction_type: newType,
            user_id: authenticatedUser.userId,
            recommendation_id: interactionItem.recommendation_id || "",
            spot_id: interactionItem.spot_id,
            data: JSON.stringify(metaData)
          }
        });

        // Track GA event for type change
        trackSpotInteraction({
          user_id: authenticatedUser.userId,
          spot_id: interactionItem.spot_id,
          spot_name: spotName || "Unknown",
          recommendation_id: interactionItem.recommendation_id || "",
          interaction_type: newType === InteractionType.SAVED ? "saved" : "visited",
          action_type: "create",
          ...metaData
        });

        // アイコン強調表示をトリガー（リスト間移動時）
        if (newType === InteractionType.SAVED) {
          highlightSaved();
        } else if (newType === InteractionType.VISITED) {
          highlightVisited();
        }

        onInteractionChange?.();
      } catch (error) {
        console.error("Error changing interaction type:", error);
        throw new Error("移動に失敗しました");
      }
    },
    [
      deleteInteractionItem,
      createInteractionItem,
      user,
      location,
      spotName,
      totalRecommendations,
      onInteractionChange,
      highlightSaved,
      highlightVisited
    ]
  );

  // 興味なしインタラクションを作成
  const createDislikeInteraction = useCallback(
    async (
      spotId: string,
      interactionMethod: "disliked" | "saved_to_disliked" | "visited_to_disliked" = "disliked"
    ) => {
      // 認証チェック
      if (!requireAuth(user)) {
        return null;
      }

      // requireAuthチェック後、userは必ず存在しuserIdも存在
      const authenticatedUser = user as { userId: string; username?: string };

      try {
        // GAとAPIの両方に送るメタデータを構築
        const metaData = {
          user_latitude: location.latitude,
          user_longitude: location.longitude,
          spot_distance: spotDistance || null,
          card_position: cardPosition || null,
          total_recommendations: totalRecommendations || null,
          interaction_method: interactionMethod,
          timestamp: new Date().toISOString()
        };

        const res = await createInteractionItem({
          requestBody: {
            interaction_type: InteractionType.DISLIKED,
            user_id: authenticatedUser.userId,
            recommendation_id: recommendationId || "",
            spot_id: spotId,
            data: JSON.stringify(metaData)
          }
        });

        // Track GA event
        trackSpotInteraction({
          user_id: authenticatedUser.userId,
          spot_id: spotId,
          spot_name: spotName || "Unknown",
          recommendation_id: recommendationId || "",
          interaction_type: "disliked",
          action_type: "create",
          ...metaData
        });

        onInteractionChange?.();
        return res?.interaction_id || null;
      } catch (error) {
        console.error("Error creating dislike interaction:", error);
        throw new Error("Failed to create dislike interaction");
      }
    },
    [
      createInteractionItem,
      user,
      location,
      recommendationId,
      spotName,
      spotDistance,
      cardPosition,
      totalRecommendations,
      onInteractionChange
    ]
  );

  return {
    createInteraction,
    deleteInteraction,
    changeInteractionType,
    createDislikeInteraction
  };
};
